/**
 * @fileoverview Integrations section showing data flow from platforms to AbyssBlock.
 * @module features/landing/components/IntegrationsSection
 */

import { ReactElement, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatedBeam } from '@/components/ui/animated-beam'
import { cn } from '@/lib/utils'

/**
 * Circle node component for integration icons.
 */
function CircleNode({
  className,
  children,
  label,
}: {
  className?: string
  children: React.ReactNode
  label?: string
}): ReactElement {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn(
          'z-10 flex size-16 items-center justify-center rounded-full border-2 border-border bg-card p-3 shadow-lg',
          className
        )}
      >
        {children}
      </div>
      {label && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
  )
}

/**
 * Central node for AbyssBlock logo.
 */
function CenterNode({
  className,
  children,
  label,
}: {
  className?: string
  children: React.ReactNode
  label?: string
}): ReactElement {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn(
          'z-10 flex size-20 items-center justify-center rounded-full border-2 border-border bg-card p-3 shadow-xl',
          className
        )}
      >
        {children}
      </div>
      {label && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
  )
}

/**
 * Integrations section with animated beams showing data flow.
 *
 * @component
 */
export function IntegrationsSection(): ReactElement {
  const { t } = useTranslation('features-landing')
  const containerRef = useRef<HTMLDivElement>(null)
  const centerRef = useRef<HTMLDivElement>(null)
  const facebookRef = useRef<HTMLDivElement>(null)
  const googleRef = useRef<HTMLDivElement>(null)
  const baselinkerRef = useRef<HTMLDivElement>(null)

  return (
    <section id="integrations" className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-12">
          {/* Left column - Header */}
          <div className="w-full lg:w-1/2">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-4 text-foreground">
              {t('integrations.title')}
            </h2>
          </div>

          {/* Right column - Animated beams */}
          <div className="w-full lg:w-1/2">
            <div
              ref={containerRef}
              className="relative flex h-[300px] w-full items-center justify-center"
            >
          {/* Left side integrations */}
          <div className="absolute left-0 flex flex-col gap-6">
            <div ref={facebookRef}>
              <CircleNode label="Meta Ads">
                <img
                  src="/meta-favicon.png"
                  alt="Meta Ads"
                  className="size-8"
                />
              </CircleNode>
            </div>
            <div ref={googleRef}>
              <CircleNode label="Google Ads">
                <img
                  src="/google-favicon.png"
                  alt="Google Ads"
                  className="size-8"
                />
              </CircleNode>
            </div>
            <div ref={baselinkerRef}>
              <CircleNode label="BaseLinker">
                <img
                  src="/base-favicon.png"
                  alt="BaseLinker"
                  className="size-8"
                />
              </CircleNode>
            </div>
          </div>

          {/* Center AbyssBlock logo */}
          <div ref={centerRef}>
            <CenterNode label="AbyssBlock">
              <img
                src="/abyssblock-icon.webp"
                alt="AbyssBlock"
                className="size-12"
              />
            </CenterNode>
          </div>

          {/* Animated beams */}
          <AnimatedBeam
            containerRef={containerRef}
            fromRef={facebookRef}
            toRef={centerRef}
            duration={16}
            delay={0}
            pathColor="rgba(255, 255, 255, 0.1)"
            gradientStartColor="#808080"
            gradientStopColor="#9e9e9e"
          />
          <AnimatedBeam
            containerRef={containerRef}
            fromRef={googleRef}
            toRef={centerRef}
            duration={16}
            delay={4}
            pathColor="rgba(255, 255, 255, 0.1)"
            gradientStartColor="#808080"
            gradientStopColor="#9e9e9e"
          />
          <AnimatedBeam
            containerRef={containerRef}
            fromRef={baselinkerRef}
            toRef={centerRef}
            duration={16}
            delay={8}
            pathColor="rgba(255, 255, 255, 0.1)"
            gradientStartColor="#808080"
            gradientStopColor="#9e9e9e"
          />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
